import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  SequenceMenu,
  useModalState,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'
import { CustomerSelect } from 'Customer/index'
import { useEffect, useState } from 'react'
import invariant from 'tiny-invariant'
import { usePermissions } from 'lib/rbac'

export const DuplicateScheduleMenuItem = (props: {
  scheduleId: string
  disabled?: boolean
  onSuccess?: (newScheduleId: string) => void
  'data-testid'?: string
}) => {
  const { can } = usePermissions()
  const { isModalOpen, openModal, closeModal } = useModalState()

  if (!can('org.billing:create')) {
    return null
  }

  return (
    <>
      <SequenceMenu.Item
        uuid="duplicate-schedule"
        label="Duplicate schedule"
        data-testid={
          props['data-testid'] ??
          `schedules.${props.scheduleId}.menuItem.duplicate`
        }
        isDisabled={props.disabled}
        onClick={openModal}
      />
      <DuplicateScheduleModal
        scheduleId={props.scheduleId}
        isOpen={isModalOpen}
        onClose={closeModal}
        onSuccess={props.onSuccess}
      />
    </>
  )
}

const DuplicateScheduleModal = (props: {
  scheduleId: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: (newScheduleId: string) => void
}) => {
  const { duplicateSchedule, customer, submitDisabled, isLoading } =
    useDuplicateSchedule({
      scheduleId: props.scheduleId
    })

  return (
    <SimpleModal
      data-testid="duplicateScheduleModal"
      isOpen={props.isOpen}
      onClose={props.onClose}
      title="Duplicate schedule"
      cancelButtonText="Cancel"
      submitButtonText="Duplicate schedule"
      submittingButtonText="Duplicating schedule..."
      submitDisabled={submitDisabled}
      minHeight="112px"
      onSubmit={async () => {
        const newScheduleId = await duplicateSchedule(props.scheduleId)
        if (newScheduleId) {
          props.onSuccess?.(newScheduleId)
          return true
        }
        return false
      }}
    >
      <CustomerSelect
        data-testid="duplicateSchedule.customer.select"
        placeholder="Find or add a new customer"
        label="Customer"
        value={customer.value ?? ''}
        onChange={customer.onChange}
        isLoading={isLoading}
        styles={{ wrapper: { marginBottom: 0 } }}
      />
    </SimpleModal>
  )
}

const useDuplicateSchedule = (props: { scheduleId: string }) => {
  const { displayPromiseNotification } = useNotifications()
  const duplicationMutation = useMutation(
    dashboard20240730Client.postDuplicateBillingSchedule
  )
  const existingScheduleQuery = useQuery(
    dashboard20240730Client.getBillingSchedule,
    {
      id: props.scheduleId
    }
  )

  const [selectedCustomerId, setSelectedCustomerId] = useState(
    existingScheduleQuery.data?.customerId
  )

  useEffect(() => {
    if (existingScheduleQuery.data?.customerId) {
      setSelectedCustomerId(existingScheduleQuery.data.customerId)
    }
  }, [existingScheduleQuery.data?.customerId])

  const duplicateSchedule = async (
    scheduleId: string
  ): Promise<string | null> => {
    try {
      invariant(selectedCustomerId, 'Schedule data not found')

      const duplicatePromise = duplicationMutation.mutateAsync({
        id: scheduleId,
        customerId: selectedCustomerId
      })

      const duplicatedSchedule = await displayPromiseNotification(
        duplicatePromise,
        {
          loading: 'Duplicating schedule...',
          success: 'Schedule duplicated',
          error: 'Unable to duplicate schedule'
        }
      )

      invariant(duplicatedSchedule, 'Schedule not found')
      return duplicatedSchedule.id
    } catch {
      return null
    }
  }

  return {
    duplicateSchedule,
    customer: {
      value: selectedCustomerId,
      onChange: (value: string) => {
        setSelectedCustomerId(value)
      }
    },
    isLoading: existingScheduleQuery.isPending,
    submitDisabled: existingScheduleQuery.isPending || !selectedCustomerId
  }
}
